/**
 * ExpenseReviewQueuePage Component
 * Primary UI surface for the AI-first expense review workflow.
 * Shows pending-review expenses with inline editing and confirm/bulk-confirm actions.
 * Route: /expenses/review
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { expensesApi, expenseCategoriesApi, listProperties } from '../lib/api';
import type { Expense, ExpenseCategory } from '../types/expense.types';
import type { PropertyWithStats } from '../lib/api';
import { formatAmount, formatExpenseDate } from '../utils/expense.utils';
import { PageHeader, EmptyState } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';
import { useDomainEvents } from '../hooks/useDomainEvents';
import { useUnseenCounts } from '../hooks/useAwareness';

// =====================================================
// Helpers
// =====================================================

interface AiConfidence {
  overall: number;
  vendor: number;
  amount: number;
  date: number;
  property: number;
  unit: number;
  category: number;
}

function parseAiConfidence(raw: unknown): AiConfidence | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const n = (k: string) => (typeof obj[k] === 'number' ? (obj[k] as number) : 0);
  return {
    overall: n('overall'),
    vendor: n('vendor'),
    amount: n('amount'),
    date: n('date'),
    property: n('property'),
    unit: n('unit'),
    category: n('category'),
  };
}

function getConfidenceBadgeClasses(score: number): string {
  if (score >= 0.8) return 'bg-success/10 text-success';
  if (score >= 0.6) return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
}

function getConfidenceLabel(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function ConfidenceIndicator({ score, label }: { score: number; label: string }) {
  const badgeClasses = getConfidenceBadgeClasses(score);
  return (
    <span
      data-testid="confidence-indicator"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badgeClasses}`}
      aria-label={`${label} confidence: ${getConfidenceLabel(score)}`}
    >
      {label}: {getConfidenceLabel(score)}
    </span>
  );
}

function SourceBadge({ sourceType }: { sourceType: Expense['sourceType'] }) {
  if (sourceType === 'EMAIL') return <Badge variant="secondary" className="bg-warning/10 text-warning">Email</Badge>;
  if (sourceType === 'UPLOAD') return <Badge variant="secondary">Upload</Badge>;
  return <Badge variant="secondary">Manual</Badge>;
}

// =====================================================
// Inline edit state per expense card
// =====================================================

interface CardEditState {
  propertyId: string;
  irsCategoryId: string;
  amount: string; // display as dollars, store as string
  expenseDate: string;
  confirming: boolean;
  error: string | null;
}

function buildEditState(expense: Expense): CardEditState {
  return {
    propertyId: expense.aiPropertyIdSuggested ?? expense.propertyId ?? '',
    irsCategoryId: expense.irsCategoryAiSuggested ?? expense.irsCategoryId ?? '',
    amount: expense.amount > 0 ? (expense.amount / 100).toFixed(2) : '',
    expenseDate: expense.expenseDate,
    confirming: false,
    error: null,
  };
}

// =====================================================
// Main Component
// =====================================================

export function ExpenseReviewQueuePage() {
  const [reviewQueue, setReviewQueue] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [editStates, setEditStates] = useState<Record<string, CardEditState>>({});
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkConfirmResult, setBulkConfirmResult] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load (and reload) the review queue. Extracted so both the initial mount and the
  // ET-BR-44 live-reactive subscription below share one code path.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [queueRes, propertiesRes, categoriesRes] = await Promise.all([
        expensesApi.getReviewQueue(),
        listProperties(),
        expenseCategoriesApi.list(),
      ]);
      setReviewQueue(queueRes.expenses);
      setProperties(propertiesRes.properties);
      setCategories(categoriesRes.categories);

      const initialEditStates: Record<string, CardEditState> = {};
      for (const expense of queueRes.expenses) {
        initialEditStates[expense.id] = buildEditState(expense);
      }
      setEditStates(initialEditStates);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // ET-BR-44 — an expense entering PENDING_REVIEW from ANY producer (manual create, upload,
  // or email ingest) must appear here live, with no reload. Previously this page had no
  // useDomainEvents subscription at all and only ever loaded once on mount.
  useDomainEvents({
    'expense.review_queued': useCallback(() => {
      void load();
    }, [load]),
  });

  // DC-4/BR-16 (docs/features/change-awareness/specification.md) — per-card unseen marks,
  // "on the thing itself" rather than a portfolio-wide header dot with no entity context.
  // Display only: the mark clears when the expense's OWN detail page (`/expenses/:id`) is
  // opened (ExpenseDetailPage's markSeen effect), not from merely appearing in this list —
  // same "list shows the mark, detail page clears it" split already used for leads/escalations.
  const { data: unseenCountsData } = useUnseenCounts();
  const unseenByExpenseId = useMemo(() => new Map(
    (unseenCountsData?.byInstance ?? [])
      .filter((entry) => entry.referenceType === 'expense')
      .map((entry) => [entry.referenceId, entry.count])
  ), [unseenCountsData]);

  // Track recently removed items for fade-out animation
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Remove a confirmed expense from local state with animation
  const removeFromQueue = (id: string, action: 'confirmed' | 'deleted' = 'confirmed') => {
    setRemovingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setReviewQueue((prev) => prev.filter((e) => e.id !== id));
      setEditStates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      // Show success message briefly
      const expense = reviewQueue.find((e) => e.id === id);
      const desc = expense?.description ?? 'Expense';
      setSuccessMessage(action === 'confirmed' ? `"${desc}" confirmed` : `"${desc}" deleted`);
      setTimeout(() => setSuccessMessage(null), 3000);
      // ET-BR-44 — the nav badge reads a server count and invalidates on expense.review_queued;
      // no more window CustomEvent fan-out.
    }, 300);
  };

  // Update a field in the edit state for one expense card
  const updateEditField = (expenseId: string, field: keyof CardEditState, value: string | boolean | null) => {
    setEditStates((prev) => {
      const existing = prev[expenseId];
      if (!existing) return prev;
      return {
        ...prev,
        [expenseId]: { ...existing, [field]: value } as CardEditState,
      };
    });
  };

  // Confirm a single expense with corrections
  const handleConfirm = async (expense: Expense) => {
    const state = editStates[expense.id];
    if (!state) return;

    updateEditField(expense.id, 'confirming', true);
    updateEditField(expense.id, 'error', null);

    try {
      const amountCents = state.amount ? Math.round(parseFloat(state.amount) * 100) : undefined;
      await expensesApi.confirm(expense.id, {
        propertyId: state.propertyId || undefined,
        irsCategoryId: state.irsCategoryId || undefined,
        amount: amountCents,
        expenseDate: state.expenseDate || undefined,
      });
      removeFromQueue(expense.id, 'confirmed');
    } catch (err) {
      setEditStates((prev) => {
        const existing = prev[expense.id];
        if (!existing) return prev;
        return {
          ...prev,
          [expense.id]: {
            ...existing,
            confirming: false,
            error: err instanceof Error ? err.message : 'Failed to confirm expense',
          } as CardEditState,
        };
      });
    }
  };

  // Delete a single expense from the review queue
  const handleDelete = async (expense: Expense) => {
    setDeleteError(null);
    try {
      await expensesApi.delete(expense.id);
      setDeletingId(null);
      removeFromQueue(expense.id, 'deleted');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete expense');
    }
  };

  // Bulk confirm: only expenses where aiPropertyIdSuggested is set AND overall confidence >= 0.7
  const eligibleForBulk = reviewQueue.filter((e) => {
    if (!e.aiPropertyIdSuggested) return false;
    const confidence = parseAiConfidence(e.aiConfidence);
    return confidence ? confidence.overall >= 0.7 : false;
  });

  const handleBulkConfirm = async () => {
    if (eligibleForBulk.length === 0) return;
    setBulkConfirming(true);
    setBulkConfirmResult(null);
    try {
      const result = await expensesApi.bulkConfirm(eligibleForBulk.map((e) => e.id));
      // Remove confirmed items from local state
      for (const e of eligibleForBulk) {
        setReviewQueue((prev) => prev.filter((ex) => ex.id !== e.id));
        setEditStates((prev) => {
          const next = { ...prev };
          delete next[e.id];
          return next;
        });
      }
      setBulkConfirmResult(`Confirmed ${result.confirmedCount} expense${result.confirmedCount !== 1 ? 's' : ''}`);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Bulk confirm failed');
    } finally {
      setBulkConfirming(false);
    }
  };

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <ExpensesLayout>
    <div data-testid="review-queue-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Review Queue"
          subtitle="Confirm AI-processed expenses"
          backLink={{ to: '/expenses', label: 'Expenses' }}
          action={
            <div className="flex items-center gap-2">
              <HelpTooltip text={"Confirms all expenses where the AI identified a property with at least 70% overall confidence. You can always review individual items below instead."} />
              <Button
                onClick={handleBulkConfirm}
                disabled={bulkConfirming || eligibleForBulk.length === 0}
                data-testid="review-queue-confirm-all"
                aria-label={`Confirm all ${eligibleForBulk.length} high-confidence expenses`}
                size="sm"
              >
                {bulkConfirming
                  ? 'Confirming...'
                  : eligibleForBulk.length > 0
                  ? `Confirm All (${eligibleForBulk.length})`
                  : 'Confirm All'}
              </Button>
            </div>
          }
        />
      </div>

      {/* Contextual help */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <HelpBlock
          summary="Expenses land here when our AI needs you to double-check something. This happens when you forward a receipt by email, upload a file, or enter an expense manually."
          details={"Each expense is analyzed by AI to identify the property, tax category, amount, and date. When the AI isn't confident about a field (especially which property it belongs to), it sends the expense here for your review.\n\nWhat to do: Check each card, correct any fields the AI got wrong (especially the property), then hit Confirm. High-confidence items can be confirmed in bulk with the \"Confirm All\" button.\n\nWhat happens if you don't review: Unreviewed expenses won't appear in your reports, dashboards, or tax summaries. They'll stay here until you confirm or delete them."}
        />
      </div>

      {/* Page error */}
      {pageError && (
        <div
          role="alert"
          className="mx-[var(--page-padding-x)] p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          {pageError}
        </div>
      )}

      {/* Bulk confirm success */}
      {bulkConfirmResult && (
        <div
          role="status"
          aria-live="polite"
          className="mx-[var(--page-padding-x)] p-3 mb-4 bg-success/10 border border-success/20 rounded-md text-success text-sm"
        >
          {bulkConfirmResult}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div
          className="mx-[var(--page-padding-x)] p-3 mb-4 bg-success/10 border border-success/20 rounded-md text-success text-sm"
          role="status"
          aria-live="polite"
        >
          {successMessage}
        </div>
      )}

      {/* Empty state */}
      {reviewQueue.length === 0 && (
        <div className="px-[var(--page-padding-x)]" data-testid="review-queue-empty">
          <EmptyState
            title="No expenses pending review"
            description="All expenses have been confirmed. New expenses from email or file uploads will appear here."
            action={
              <Button asChild>
                <Link to="/expenses">View All Expenses</Link>
              </Button>
            }
          />
        </div>
      )}

      {/* Review Queue Items */}
      {reviewQueue.length > 0 && (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-4">
          {reviewQueue.map((expense) => {
            const state = editStates[expense.id];
            if (!state) return null;
            const confidence = parseAiConfidence(expense.aiConfidence);
            const suggestedProperty = properties.find(
              (p) => p.id === expense.aiPropertyIdSuggested
            );
            const unseenCount = unseenByExpenseId.get(expense.id) ?? 0;

            return (
              <div
                key={expense.id}
                className="rounded-lg p-4 border border-border bg-card transition-all duration-300"
                style={{
                  opacity: removingIds.has(expense.id) ? 0 : 1,
                  transform: removingIds.has(expense.id) ? 'translateX(20px)' : 'none',
                }}
                data-testid={`review-queue-item-${expense.id}`}
              >
                {/* Card Header */}
                <div className="flex justify-between items-start mb-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className="text-xl font-bold text-foreground"
                        aria-label={`Amount: ${formatAmount(expense.amount)}`}
                      >
                        {formatAmount(expense.amount)}
                      </span>
                      <SourceBadge sourceType={expense.sourceType} />
                      {unseenCount > 0 && (
                        <span
                          data-testid={`review-queue-item-unseen-${expense.id}`}
                          aria-label={`${unseenCount} unseen update${unseenCount === 1 ? '' : 's'}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
                          {unseenCount > 1 ? `${unseenCount > 99 ? '99+' : unseenCount} new` : 'New'}
                        </span>
                      )}
                    </div>
                    <p className="m-0 text-sm font-medium text-foreground">
                      {expense.description}
                    </p>
                    {expense.vendorName && (
                      <p className="m-0 mt-1 text-xs text-muted-foreground">
                        Vendor: {expense.vendorName}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/expenses/${expense.id}`}>
                        View Details
                      </Link>
                    </Button>
                  </div>
                </div>

                {/* AI Suggestions Row */}
                <div className="p-3 bg-accent/50 border border-border rounded-md mb-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <p className="m-0 text-xs font-semibold text-primary uppercase tracking-wide">
                      AI Analysis
                    </p>
                    <HelpTooltip text={"These are the AI's best guesses based on the receipt or invoice you submitted. The percentage scores show how confident the AI is about each field. Green (80%+) means very likely correct. Yellow (60-79%) means uncertain. Red (below 60%) means you should check carefully."} />
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-foreground">
                    <div>
                      <span className="text-muted-foreground">Property: </span>
                      <strong>{suggestedProperty?.name ?? (expense.aiPropertyIdSuggested ? 'Unknown property' : 'Not identified')}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category: </span>
                      <strong>
                        {expense.irsCategoryAiSuggested
                          ? (categories.find((c) => c.id === expense.irsCategoryAiSuggested)?.name ?? expense.irsCategoryAiSuggested)
                          : 'None'}
                      </strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date: </span>
                      <strong>{formatExpenseDate(expense.expenseDate)}</strong>
                    </div>
                  </div>
                  {confidence && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <ConfidenceIndicator score={confidence.overall} label="Overall" />
                      <ConfidenceIndicator score={confidence.property} label="Property" />
                      <ConfidenceIndicator score={confidence.category} label="Category" />
                    </div>
                  )}
                </div>

                {/* Inline Editing Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {/* Property picker */}
                  <div>
                    <label
                      htmlFor={`review-property-${expense.id}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      Property *
                    </label>
                    <select
                      id={`review-property-${expense.id}`}
                      value={state.propertyId}
                      onChange={(e) => updateEditField(expense.id, 'propertyId', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                      data-testid={`review-queue-item-property-select-${expense.id}`}
                      aria-label="Select property for this expense"
                    >
                      <option value="">-- Select Property --</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* IRS Category */}
                  <div>
                    <label
                      htmlFor={`review-category-${expense.id}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      IRS Category
                    </label>
                    <select
                      id={`review-category-${expense.id}`}
                      value={state.irsCategoryId}
                      onChange={(e) => updateEditField(expense.id, 'irsCategoryId', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                      data-testid={`review-queue-item-category-select-${expense.id}`}
                      aria-label="Select IRS category for this expense"
                    >
                      <option value="">-- Uncategorized --</option>
                      {sortedCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label
                      htmlFor={`review-amount-${expense.id}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      Amount ($)
                    </label>
                    <input
                      id={`review-amount-${expense.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={state.amount}
                      onChange={(e) => updateEditField(expense.id, 'amount', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                      data-testid={`review-queue-item-amount-${expense.id}`}
                      aria-label="Expense amount in dollars"
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label
                      htmlFor={`review-date-${expense.id}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      Date
                    </label>
                    <input
                      id={`review-date-${expense.id}`}
                      type="date"
                      value={state.expenseDate}
                      onChange={(e) => updateEditField(expense.id, 'expenseDate', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                      data-testid={`review-queue-item-date-${expense.id}`}
                      aria-label="Expense date"
                    />
                  </div>
                </div>

                {/* Error state */}
                {state.error && (
                  <p role="alert" className="m-0 mb-3 text-xs text-destructive">
                    {state.error}
                  </p>
                )}

                {deleteError && deletingId === expense.id && (
                  <p role="alert" className="m-0 mb-3 text-xs text-destructive">
                    {deleteError}
                  </p>
                )}

                {/* Card Footer: Delete confirm or action buttons */}
                {deletingId === expense.id ? (
                  <div className="flex justify-end items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground">
                      Delete <strong>{expense.description}</strong>? This cannot be undone.
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDeletingId(null); setDeleteError(null); }}
                      aria-label="Cancel delete"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(expense)}
                      data-testid={`review-queue-item-delete-confirm-${expense.id}`}
                      aria-label={`Confirm delete expense: ${expense.description}`}
                    >
                      Delete
                    </Button>
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setDeletingId(expense.id); setDeleteError(null); }}
                      data-testid={`review-queue-item-delete-${expense.id}`}
                      aria-label={`Delete expense: ${expense.description}`}
                      className="text-destructive hover:text-destructive"
                    >
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleConfirm(expense)}
                      disabled={state.confirming || !state.propertyId}
                      data-testid={`review-queue-item-confirm-${expense.id}`}
                      aria-label={`Confirm expense: ${expense.description}`}
                    >
                      {state.confirming ? 'Confirming...' : 'Confirm'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </ExpensesLayout>
  );
}

export default ExpenseReviewQueuePage;
